/**
 * The icon set.
 *
 * Drawn rather than borrowed, on a 24-unit grid with a uniform 1.6 stroke,
 * round caps and round joins. Consistency in those three values is most of what
 * separates a considered icon set from an assembled one — and emoji, which is
 * the single loudest tell that nobody drew the interface.
 *
 * Icons are decorative by default (`aria-hidden`), because every control that
 * uses one also carries a text label or an `aria-label`. An icon that carries
 * meaning on its own has failed §26 before it is drawn.
 */
import type { SVGProps } from 'react';

export type IconName =
  // documents
  | 'file' | 'file-text' | 'file-sheet' | 'file-slides' | 'file-image'
  | 'file-archive' | 'file-media' | 'file-code' | 'file-pdf'
  // navigation
  | 'chevron-left' | 'chevron-right' | 'chevron-down' | 'chevron-up'
  | 'sidebar' | 'panel-right' | 'close' | 'search' | 'arrow-left'
  // view
  | 'zoom-in' | 'zoom-out' | 'fit-width' | 'fit-page' | 'actual-size'
  | 'fullscreen' | 'rotate' | 'grid' | 'list'
  // editing
  | 'edit' | 'undo' | 'redo' | 'crop' | 'highlight' | 'pen' | 'note'
  | 'text' | 'shapes' | 'mosaic' | 'adjust' | 'eraser'
  // file actions
  | 'save' | 'save-as' | 'share' | 'print' | 'open' | 'download' | 'plus'
  // status
  | 'info' | 'alert' | 'shield' | 'lock' | 'check' | 'clock' | 'eye' | 'eye-off'
  // collections
  | 'star' | 'star-filled'
  // theme and console
  | 'sun' | 'moon' | 'command' | 'globe' | 'chart' | 'database';

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  readonly name: IconName;
  readonly size?: number;
  /** Supply only when the icon is the sole carrier of meaning — normally it is not. */
  readonly title?: string;
}

/* Paths are authored as stroke geometry so a single set of stroke attributes
   governs every glyph; no icon carries its own weight. */
const PATHS: Record<IconName, string> = {
  file: 'M14 3v5h5M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z',
  'file-text': 'M14 3v5h5M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5ZM9 13h6M9 17h4',
  'file-sheet': 'M14 3v5h5M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5ZM8 12h8M8 16h8M12 12v8',
  'file-slides': 'M14 3v5h5M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5ZM8 12h8v5H8z',
  'file-image': 'M14 3v5h5M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5ZM9.5 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM8 18l3-3 2 2 2-2 2 2',
  'file-archive': 'M14 3v5h5M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5ZM10 6v1M10 9v1M10 12v1M10 15h1.5v3H10z',
  'file-media': 'M14 3v5h5M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5ZM10.5 12.5v5l4-2.5z',
  'file-code': 'M14 3v5h5M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5ZM10 12l-2 2.5 2 2.5M14 12l2 2.5-2 2.5',
  'file-pdf': 'M14 3v5h5M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5ZM8.5 17.5c2-1 3.2-3 4-5.5.5-1.6 1.6-1.4 1.7-.4.1 1.3-2.2 2-4.4 2.6-1.4.4-2.5.7-1.3 3.3Z',

  'chevron-left': 'm14.5 6-6 6 6 6',
  'chevron-right': 'm9.5 6 6 6-6 6',
  'chevron-down': 'm6 9.5 6 6 6-6',
  'chevron-up': 'm6 14.5 6-6 6 6',
  'arrow-left': 'M19 12H5m0 0 6-6m-6 6 6 6',
  sidebar: 'M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5zM9.5 4v16',
  'panel-right': 'M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5zM14.5 4v16',
  close: 'm6 6 12 12M18 6 6 18',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM16 16l4.5 4.5',

  'zoom-in': 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM16 16l4.5 4.5M8.5 11h5M11 8.5v5',
  'zoom-out': 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM16 16l4.5 4.5M8.5 11h5',
  'fit-width': 'M4 6v12M20 6v12M7.5 12h9m0 0-2.5-2.5M16.5 12 14 14.5M7.5 12l2.5-2.5M7.5 12 10 14.5',
  'fit-page': 'M5 8V6a1 1 0 0 1 1-1h2M19 8V6a1 1 0 0 0-1-1h-2M5 16v2a1 1 0 0 0 1 1h2M19 16v2a1 1 0 0 1-1 1h-2',
  'actual-size': 'M4.5 5.5h15v13h-15zM9 10h1.5v4M13 10h2v4h-2z',
  fullscreen: 'M4 9V5a1 1 0 0 1 1-1h4M20 9V5a1 1 0 0 0-1-1h-4M4 15v4a1 1 0 0 0 1 1h4M20 15v4a1 1 0 0 1-1 1h-4',
  rotate: 'M20 11a8 8 0 1 1-2.6-5.9M20 4v5h-5',
  grid: 'M4.5 4.5h6v6h-6zM13.5 4.5h6v6h-6zM4.5 13.5h6v6h-6zM13.5 13.5h6v6h-6z',
  list: 'M8 6h12M8 12h12M8 18h12M4.5 6h.01M4.5 12h.01M4.5 18h.01',

  edit: 'M12 20h8M4 20l1-4L16.5 4.5a2.1 2.1 0 0 1 3 3L8 19z',
  undo: 'M4 9h11a5 5 0 0 1 0 10h-6M4 9l4-4M4 9l4 4',
  redo: 'M20 9H9a5 5 0 0 0 0 10h6M20 9l-4-4M20 9l-4 4',
  crop: 'M6.5 2v15.5H22M2 6.5h15.5V22',
  highlight: 'M14 5.5 18.5 10 10 18.5H5.5V14zM3 21.5h18',
  pen: 'M4 20l1-4L16.5 4.5a2.1 2.1 0 0 1 3 3L8 19z',
  note: 'M5 5.5A1.5 1.5 0 0 1 6.5 4h11A1.5 1.5 0 0 1 19 5.5v9L14.5 19h-8A1.5 1.5 0 0 1 5 17.5zM19 14.5h-4.5V19',
  text: 'M5 7V5h14v2M12 5v14M9 19h6',
  shapes: 'M4.5 13.5h6v6h-6zM16.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
  mosaic: 'M4.5 4.5h5v5h-5zM14.5 4.5h5v5h-5zM9.5 9.5h5v5h-5zM4.5 14.5h5v5h-5zM14.5 14.5h5v5h-5z',
  adjust: 'M6 4v6M6 14v6M12 4v3M12 11v9M18 4v9M18 17v3M3.5 12h5M9.5 9h5M15.5 15h5',
  eraser: 'm14 5.5 4.5 4.5-8 8H6L3.5 15zM9 19h11',

  save: 'M5 5.5A1.5 1.5 0 0 1 6.5 4h9L20 8.5v10a1.5 1.5 0 0 1-1.5 1.5h-12A1.5 1.5 0 0 1 5 18.5zM8.5 4v5h6M8 20v-5h8v5',
  'save-as': 'M5 5.5A1.5 1.5 0 0 1 6.5 4H14l5 5v3M5 5.5v13A1.5 1.5 0 0 0 6.5 20H12M18 15v6M15 18h6',
  share: 'M12 15V4m0 0L8 8m4-4 4 4M5 14v4.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V14',
  print: 'M7 9V4.5A.5.5 0 0 1 7.5 4h9a.5.5 0 0 1 .5.5V9M7 17H5.5A1.5 1.5 0 0 1 4 15.5v-5A1.5 1.5 0 0 1 5.5 9h13a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 1-1.5 1.5H17M7 14h10v5.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5z',
  open: 'M4 8V6a1.5 1.5 0 0 1 1.5-1.5h3.2a1.5 1.5 0 0 1 1.2.6l.9 1.2a1.5 1.5 0 0 0 1.2.6h5a1.5 1.5 0 0 1 1.5 1.5v.6M3.6 10.4A1 1 0 0 1 4.6 9.2h14.8a1 1 0 0 1 1 1.2l-1.2 7a1.5 1.5 0 0 1-1.5 1.3H6.3a1.5 1.5 0 0 1-1.5-1.3z',
  download: 'M12 4v11m0 0 4-4m-4 4-4-4M5 19h14',
  plus: 'M12 5v14M5 12h14',

  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 11v5M12 7.8h.01',
  alert: 'M12 3.5 2.8 19.5h18.4zM12 9.5v4.5M12 17h.01',
  shield: 'M12 3.5 5 6.2v5.1c0 4.3 2.9 7.6 7 9.2 4.1-1.6 7-4.9 7-9.2V6.2z',
  lock: 'M7 10V7.5a5 5 0 0 1 10 0V10M5.5 10h13a.5.5 0 0 1 .5.5v9a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5Z',
  check: 'm5 12.5 4.5 4.5L19 7',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7.5V12l3 2',
  eye: 'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12ZM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  'eye-off': 'M4 4l16 16M9.9 5.9A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-3.2 4M6.6 8.1A17 17 0 0 0 2.5 12S6 18.5 12 18.5c.8 0 1.5-.1 2.2-.3M9.9 9.9a3 3 0 0 0 4.2 4.2',

  // One geometry for both; `star-filled` is filled at render time, so the
  // pinned and unpinned states are the same shape and read as one control.
  star: 'M12 3.25l2.47 5.26 5.78.82-4.2 4.02 1.02 5.72L12 16.4l-5.07 2.68 1.02-5.72-4.2-4.02 5.78-.82z',
  'star-filled': 'M12 3.25l2.47 5.26 5.78.82-4.2 4.02 1.02 5.72L12 16.4l-5.07 2.68 1.02-5.72-4.2-4.02 5.78-.82z',

  sun: 'M12 16.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9ZM12 2.5v2M12 19.5v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2.5 12h2M19.5 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
  moon: 'M20 14.3A8.5 8.5 0 0 1 9.7 4 8.5 8.5 0 1 0 20 14.3Z',
  command: 'M9 6a2.5 2.5 0 1 0-2.5 2.5H9zm0 0h6m-6 0v6m6-6a2.5 2.5 0 1 1 2.5 2.5H15zm0 0v6m0 0h-6m6 0a2.5 2.5 0 1 0 2.5-2.5H15zm-6 0a2.5 2.5 0 1 1-2.5-2.5H9z',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3.5 9.5h17M3.5 14.5h17M12 3a14 14 0 0 0 0 18M12 3a14 14 0 0 1 0 18',
  chart: 'M4 20h16M7.5 20v-6M12 20V8M16.5 20v-9',
  database: 'M12 8.5c4.1 0 7.5-1.2 7.5-2.75S16.1 3 12 3 4.5 4.2 4.5 5.75 7.9 8.5 12 8.5ZM4.5 5.75v12.5C4.5 19.8 7.9 21 12 21s7.5-1.2 7.5-2.75V5.75M4.5 12c0 1.55 3.4 2.75 7.5 2.75s7.5-1.2 7.5-2.75',
};

export function Icon({ name, size = 18, title, ...rest }: IconProps): React.JSX.Element {
  // A handful of glyphs are solid (a filled star for a pinned favourite). The
  // convention is the name: `-filled` paints, everything else strokes.
  const filled = name.endsWith('-filled');
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d={PATHS[name]} />
    </svg>
  );
}

/** Icon for a format, chosen from its family rather than its extension. */
export function iconForFormat(formatId: string | null, family?: string): IconName {
  if (formatId === 'pdf') return 'file-pdf';
  switch (family) {
    case 'spreadsheet': return 'file-sheet';
    case 'presentation': return 'file-slides';
    case 'image': return 'file-image';
    case 'archive': return 'file-archive';
    case 'video':
    case 'audio': return 'file-media';
    case 'code':
    case 'data': return 'file-code';
    case 'document': return 'file-text';
    default: return 'file';
  }
}
